import { useEffect, useState } from 'react';
import { getAccessToken } from '../lib/auth';

interface AccessUser {
  systemuserid: string;
  fullname: string;
  title: string | null;
  territory: string | null;
  enabled: boolean;
  canSignIn: boolean;
}

type State =
  | { status: 'loading' }
  | { status: 'ok'; users: AccessUser[] }
  | { status: 'error'; message: string };

interface Props {
  onClose: () => void;
}

/**
 * Leadership-only settings: toggle the Director/Leadership (see-everything)
 * view for eligible people — anyone with a Lead/Leader/Team Lead/Director
 * title in Mercury, or a member of the Bristol Finance and Compliance team.
 */
export function SettingsPanel({ onClose }: Props) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Could not acquire a sign-in token');
        const res = await fetch('/api/access', { headers: { 'X-User-Token': token } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { users: AccessUser[] };
        if (!cancelled) setState({ status: 'ok', users: body.users });
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (user: AccessUser) => {
    if (state.status !== 'ok' || saving) return;
    const next = !user.enabled;
    setSaving(user.systemuserid);
    setSaveError(null);
    // Optimistic update; revert on failure.
    setState({
      status: 'ok',
      users: state.users.map((u) =>
        u.systemuserid === user.systemuserid ? { ...u, enabled: next } : u,
      ),
    });
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Could not acquire a sign-in token');
      const res = await fetch('/api/access', {
        method: 'POST',
        headers: { 'X-User-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ systemuserid: user.systemuserid, enabled: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      setState((prev) =>
        prev.status === 'ok'
          ? {
              status: 'ok',
              users: prev.users.map((u) =>
                u.systemuserid === user.systemuserid ? { ...u, enabled: user.enabled } : u,
              ),
            }
          : prev,
      );
      setSaveError(
        `Couldn't save ${user.fullname}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="ds-modal-backdrop" onClick={onClose}>
      <div className="ds-modal" style={{ width: 'min(680px, 100%)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <header style={{ display: 'flex', justifyContent: 'space-between', gap: 16, position: 'sticky', top: 0, background: 'var(--panel)', zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Settings</div>
            <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: '-0.01em' }}>View access</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
              Grant the leadership (all-teams) view. Eligible: Lead / Director titles and the Bristol Finance and
              Compliance team.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ color: 'var(--text-3)', fontSize: 22, lineHeight: 1, alignSelf: 'flex-start' }}>
            ×
          </button>
        </header>

        <div style={{ padding: 20 }}>
          {state.status === 'loading' && (
            <div style={{ color: 'var(--text-2)', fontSize: 13, padding: '32px 0', textAlign: 'center' }}>Loading…</div>
          )}
          {state.status === 'error' && (
            <div style={{ color: 'var(--rag-red-ink)', fontSize: 13, padding: '32px 0', textAlign: 'center' }}>
              Couldn&rsquo;t load: {state.message}
            </div>
          )}
          {state.status === 'ok' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {state.users.map((u) => (
                <div key={u.systemuserid} className="settings-row">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.fullname}
                      {!u.canSignIn && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--rag-red-ink)' }}>no AAD account</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.title ?? '—'}
                      {u.territory ? ` · ${u.territory}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{ width: 24, textAlign: 'right', fontSize: 12, fontWeight: 600, color: u.enabled ? 'var(--rag-green-ink)' : 'var(--text-3)' }}>
                      {u.enabled ? 'On' : 'Off'}
                    </span>
                    <button
                      onClick={() => toggle(u)}
                      disabled={saving === u.systemuserid}
                      aria-label={`Toggle access for ${u.fullname}`}
                      aria-pressed={u.enabled}
                      className={'ds-switch' + (u.enabled ? ' on' : '')}
                    >
                      <span />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {saveError && <div style={{ marginTop: 14, fontSize: 12, color: 'var(--rag-red-ink)' }}>{saveError}</div>}
          <p style={{ marginTop: 20, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
            The configured directors (Jonny; Andrew and Jim) and the core leadership list are set in the app settings and
            always have access, regardless of these toggles.
          </p>
        </div>
      </div>
    </div>
  );
}